import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { mappingService } from '../../services/mappingService';

/*
 * Generic mapping modal — works for any import type.
 * Pass a `config` object from importConfig.js (title, importType, columns).
 * Replaces the old GLMappingModal (which hardcoded GL columns + import_type).
 */
const MappingModal = ({ isOpen, onClose, config }) => {
  const [fileHeaders, setFileHeaders] = useState([]);
  const [mappings, setMappings] = useState({});
  const [fileName, setFileName] = useState('');
  const [mappingVersion, setMappingVersion] = useState('');
  const [versionError, setVersionError] = useState('');
  const fileInputRef = useRef(null);

  if (!isOpen || !config) return null;

  const dbColumns = config.columns;

  const handleFileUpload = () => {
    const inputElement = fileInputRef.current;
    if (!inputElement || !inputElement.files || inputElement.files.length === 0) return;

    const file = inputElement.files[0];
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData && jsonData.length > 0) {
          const headers = jsonData[0].filter(Boolean).map(String);
          setFileHeaders(headers);
        } else {
          alert('The file appears to be empty.');
        }
      } catch (error) {
        console.error('Parsing Error:', error);
        alert('Failed to parse the file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSelectMapping = (dbColumnId, fileHeader) => {
    setMappings((prev) => ({ ...prev, [dbColumnId]: fileHeader }));
  };

  const handleVersionChange = (e) => {
    const value = e.target.value;
    setMappingVersion(value);
    if (versionError && value.trim()) setVersionError('');
  };

  // Check that every required DB column has been mapped to a file header
  const missingRequired = dbColumns
    .filter((c) => c.required)
    .filter((c) => !mappings[c.id]);

  const resetState = () => {
    setMappingVersion('');
    setFileHeaders([]);
    setMappings({});
    setVersionError('');
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!mappingVersion.trim()) {
      setVersionError('Please enter a mapping version name before saving.');
      return;
    }
    if (missingRequired.length > 0) {
      alert(
        `Please map all required columns before saving:\n` +
          missingRequired.map((c) => `• ${c.label}`).join('\n')
      );
      return;
    }

    try {
      const payload = {
        version: mappingVersion.trim(),
        import_type: config.importType,
        mappings,
      };

      await mappingService.saveMapping(payload);

      alert('Mappings successfully saved to the database!');
      resetState();
      onClose();
    } catch (error) {
      console.error('Save failed:', error);
      alert('Failed to save mappings: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRemoveFile = () => {
    setFileHeaders([]);
    setFileName('');
    setMappings({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden bg-white rounded-xl shadow-2xl flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Map {config.title}</h2>
            <p className="text-sm text-slate-500">Assign your file headers to the Mantle database columns.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
          {fileHeaders.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-slate-300 rounded-lg bg-white">
              <svg className="w-12 h-12 text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <label className="cursor-pointer px-4 py-2 bg-slate-900 text-white rounded-md font-medium text-sm hover:bg-slate-800 transition-colors">
                Browse for Excel File
                <input
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                />
              </label>
              <p className="mt-2 text-xs text-slate-500">Supports .xlsx, .xls, and .csv</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">

              {/* File info bar */}
              <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
                <span className="text-sm font-medium text-slate-700">
                  Loaded File: <span className="font-bold">{fileName}</span>
                </span>
                <button
                  onClick={handleRemoveFile}
                  className="text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  Remove File
                </button>
              </div>

              {/* Mapping Version Input */}
              <div className="p-4 border-b border-slate-200 bg-white space-y-1">
                <label className="block text-sm font-semibold text-slate-800">
                  Mapping Version Name <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-slate-400">
                  Give this mapping a unique name so you can identify or reuse it later (e.g. "v1.0" or "Standard Sage Export").
                </p>
                <input
                  type="text"
                  placeholder="e.g., v1.0 or Standard Export"
                  className={`w-full text-sm rounded-md shadow-sm p-2 border outline-none transition-colors
                    ${versionError
                      ? 'border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-400 bg-red-50'
                      : 'border-slate-300 focus:border-slate-500 focus:ring-1 focus:ring-slate-400 bg-white'
                    }`}
                  value={mappingVersion}
                  onChange={handleVersionChange}
                />
                {versionError && (
                  <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                    <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {versionError}
                  </p>
                )}
              </div>

              {/* Column mapping rows */}
              <div className="divide-y divide-slate-100">
                {dbColumns.map((col) => (
                  <div key={col.id} className="grid grid-cols-2 gap-6 p-4 items-center hover:bg-slate-50 transition-colors">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 text-sm">{col.label}</span>
                        {col.required && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 uppercase tracking-wider">
                            Required
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400 font-mono mt-1 block">{col.id}</span>
                    </div>
                    <div>
                      <select
                        className="w-full text-sm border-slate-300 rounded-md shadow-sm focus:border-slate-500 focus:ring-slate-500 bg-white p-2 border outline-none"
                        value={mappings[col.id] || ''}
                        onChange={(e) => handleSelectMapping(col.id, e.target.value)}
                      >
                        <option value="">-- Ignore this column --</option>
                        {fileHeaders.map((header, idx) => (
                          <option key={idx} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-transparent hover:bg-slate-50 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            disabled={fileHeaders.length === 0 || mappingVersion.trim() === '' || missingRequired.length > 0}
            onClick={handleSave}
            className="px-6 py-2 text-sm font-medium text-white bg-slate-900 rounded-md hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Confirm Mapping
          </button>
        </div>
      </div>
    </div>
  );
};

export default MappingModal; 